import type { FinancialPerformanceIndicatorsResponse, TreeFPIContribution } from 'venn-api';
import { useMemo } from 'react';
import { type FPITreeRow } from '../../../../studio-blocks/types';
import { formatData } from '../../../../data-grid';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { getComparisonLabelForBlockLegend } from 'venn-utils';
import HeaderCellRenderer from '../../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import { get } from 'lodash';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { getDefaultCellClass } from '../../../../studio-blocks/logic/gridStyling';
import { useRecoilValue } from 'recoil';
import { blockLimitedHoldingsSubjectsWithFees } from 'venn-state';
import { useSelectedMetrics } from '../../../../studio-blocks/logic/useSelectedMetrics';

type RowMap = { [path: string]: FPITreeRow };

// This contains null byte so should be safe to use as separator
const SEPARATOR = '#\u0000#';

const fillTreeData = (
  contributions: TreeFPIContribution,
  currentPath: string[],
  rowMap: RowMap,
  subjectIndex: number,
) => {
  const node = contributions.value;
  const name = currentPath.length === 0 ? 'Total' : node.name;
  const path = [...currentPath, name];
  const pathKey = path.join(SEPARATOR);

  if (!rowMap[pathKey]) {
    rowMap[pathKey] = {
      key: pathKey,
      label: name,
      path,
      value: [],
      isStrategy: node.id.type !== 'fund',
    };
  }

  rowMap[pathKey].value[subjectIndex] = node.metadata.financialPerformanceIndicators;

  contributions.children.forEach((child) => fillTreeData(child, path, rowMap, subjectIndex));
};

const getTreeData = (data: FinancialPerformanceIndicatorsResponse): FPITreeRow[] => {
  const rowMap = {};
  data.contributions.forEach((contribution, index) => fillTreeData(contribution, [], rowMap, index));
  return Object.values(rowMap);
};

export const useFinancialPerformanceIndicatorsTreeTable = (
  id: string,
  data: FinancialPerformanceIndicatorsResponse,
) => {
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(id));

  const selectedMetrics = useSelectedMetrics();

  const rowData = useMemo((): FPITreeRow[] => getTreeData(data), [data]);

  const columnDefs = useMemo(
    (): ColGroupDef<FPITreeRow>[] =>
      allSubjects.map((subject, subjectIndex) => ({
        marryChildren: true,
        headerName: getComparisonLabelForBlockLegend(subject),
        headerGroupComponent: HeaderCellRenderer,
        headerGroupComponentParams: {
          subject,
        },
        children: selectedMetrics.map(
          (metric): ColDef<FPITreeRow> => ({
            headerName: metric.label,
            valueGetter: ({ data }) => get(data?.value[subjectIndex], metric.metricPath!),
            valueFormatter: ({ value }) => formatData(value, metric.dataType),
            cellRenderer: ValueCellRenderer,
            cellClass: getDefaultCellClass,
            autoHeight: false,
          }),
        ),
      })),
    [allSubjects, selectedMetrics],
  );

  return { rowData, columnDefs };
};
